import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resolveImage } from "@/lib/api";
import { Card, Btn, useSave, AInput, ATextarea, ASelect, labelCls, inputCls } from "../AdminUI";
import { SOCIAL_PLATFORMS, activeSocials } from "@/components/site/SocialLinks";
import MediaPicker from "@/components/admin/MediaPicker";

const SocialLinksCard = ({ c, setC }) => {
  const sl = c.social_links || {};
  const norm = (v) => (typeof v === "string" ? { url: v, enabled: !!v } : { url: v?.url || "", enabled: !!v?.enabled });
  const setField = (key, patch) => {
    const cur = norm(sl[key]);
    setC({ ...c, social_links: { ...sl, [key]: { ...cur, ...patch } } });
  };
  const preview = activeSocials(sl);
  return (
    <Card title="Social Media">
      <p className="text-xs text-gray-400 mb-3">Add your account URLs and toggle each on. Only enabled platforms with a valid https link appear in the public footer. Facebook, Instagram and TikTok are the primary platforms; the rest are optional.</p>
      <div className="space-y-3">
        {SOCIAL_PLATFORMS.map((p) => {
          const v = norm(sl[p.key]);
          return (
            <div key={p.key} className="grid grid-cols-1 sm:grid-cols-[130px_1fr_auto] items-center gap-2">
              <span className="text-sm font-semibold text-[var(--navy)]">{p.label}</span>
              <input className={inputCls} placeholder={`https://${p.key === "x" ? "x.com" : p.key + ".com"}/...`}
                value={v.url} onChange={(e) => setField(p.key, { url: e.target.value })} data-testid={`social-${p.key}-url`} />
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={v.enabled} onChange={(e) => setField(p.key, { enabled: e.target.checked })}
                  className="w-4 h-4 accent-[var(--action)]" data-testid={`social-${p.key}-enabled`} /> Enabled
              </label>
            </div>
          );
        })}
      </div>
      <div className="mt-4 rounded-xl bg-[var(--navy)] p-4" data-testid="social-preview">
        <p className="overline text-[var(--aqua)] mb-2 text-xs">Footer Preview</p>
        {preview.length === 0 ? (
          <p className="text-white/50 text-sm">No social links will show until at least one platform is enabled with a valid URL.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {preview.map((p) => (
              <span key={p.key} className="text-white/90 text-xs bg-white/10 rounded-full px-3 py-1">{p.label}</span>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-3">Click <b>Save Changes</b> at the top to publish. Future URL updates need no code or redeploy.</p>
    </Card>
  );
};

const POS_OPTIONS = [
  { value: "center", label: "Center" },
  { value: "center top", label: "Top" },
  { value: "center bottom", label: "Bottom" },
  { value: "left center", label: "Left" },
  { value: "right center", label: "Right" },
  { value: "left top", label: "Top Left" },
  { value: "right top", label: "Top Right" },
  { value: "left bottom", label: "Bottom Left" },
  { value: "right bottom", label: "Bottom Right" },
];
const DEFAULT_HERO = "/gallery/hero.jpg";
const DEFAULT_LOGO = "/brand/logo.png";

const LogoCard = ({ c, setC }) => {
  const logoSrc = c.site_logo ? resolveImage(c.site_logo) : DEFAULT_LOGO;
  const reset = () => {
    if (!window.confirm("Reset the website logo to the original Veterans PWC logo?")) return;
    setC((prev) => ({ ...prev, site_logo: "" }));
    toast.success("Reset — click Save Changes to publish");
  };
  return (
    <Card title="Website Logo">
      <div className="space-y-4">
        <MediaPicker
          label="Current Logo"
          value={c.site_logo || ""}
          onChange={(ref) => setC((prev) => ({ ...prev, site_logo: ref }))}
          previewFit="contain"
          previewClass="h-32 bg-white"
          hint="Recommended: transparent PNG or WEBP. Aspect ratio is preserved — wide or square both fit."
          testid="site-logo"
        />

        <div>
          <label className={labelCls}>Header & Footer Preview</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid="logo-desktop-preview">
              <div className="text-[10px] text-gray-400 px-3 pt-2 uppercase tracking-wide">Desktop Header</div>
              <div className="flex items-center gap-3 px-4 py-3 bg-white">
                <img src={logoSrc} alt="" className="h-14 w-auto max-w-[180px] rounded-lg object-contain" />
                <span className="leading-[1.05]">
                  <span className="block font-head font-bold text-[var(--navy)] text-lg uppercase tracking-tight">Veterans Power Wash</span>
                  <span className="block font-head font-semibold text-[var(--text-muted)] text-[11px] uppercase tracking-wide">& Cleaning Services</span>
                </span>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid="logo-mobile-preview">
              <div className="text-[10px] text-gray-400 px-3 pt-2 uppercase tracking-wide">Mobile Header</div>
              <div className="flex items-center justify-between px-3 py-3 bg-white">
                <img src={logoSrc} alt="" className="h-12 w-auto max-w-[130px] rounded-lg object-contain" />
                <div className="flex items-center gap-2 text-[var(--navy)]">
                  <span className="w-6 h-0.5 bg-current block relative before:content-[''] before:absolute before:-top-2 before:left-0 before:w-6 before:h-0.5 before:bg-current after:content-[''] after:absolute after:top-2 after:left-0 after:w-6 after:h-0.5 after:bg-current" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Btn variant="ghost" onClick={reset} data-testid="logo-reset">Reset to Default</Btn>
        </div>
        <p className="text-xs text-gray-400">{c.site_logo ? "Custom logo active" : "Using the default Veterans PWC logo"}. Click <b>Save Changes</b> at the top to publish. Future logo changes need no code or redeploy.</p>
      </div>
    </Card>
  );
};

const HeroImageCard = ({ c, setC }) => {
  const reset = () => {
    if (!window.confirm("Reset the homepage hero to the original default image?")) return;
    setC((prev) => ({ ...prev, homepage_hero_image: "", homepage_hero_position: "center", homepage_hero_mobile_image: "", homepage_hero_mobile_position: "", homepage_hero_overlay: 0 }));
    toast.success("Reset — click Save Changes to publish");
  };
  const heroSrc = c.homepage_hero_image ? resolveImage(c.homepage_hero_image) : DEFAULT_HERO;
  return (
    <Card title="Homepage Hero">
      <div className="space-y-4">
        <div>
          <label className={labelCls}>Current Hero Background</label>
          <div className="relative h-56 rounded-xl overflow-hidden border border-gray-200" data-testid="hero-preview">
            <img src={heroSrc} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: c.homepage_hero_position || "center" }} />
            <div className="absolute inset-0 hero-overlay" />
            {c.homepage_hero_overlay > 0 && <div className="absolute inset-0" style={{ background: `rgba(10,25,47,${Math.min(Number(c.homepage_hero_overlay), 80) / 100})` }} />}
            <div className="relative h-full flex flex-col justify-center px-5">
              <span className="inline-flex w-fit items-center gap-1 bg-[var(--red)] text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase">Veteran-Owned · Orlando, FL</span>
              <p className="text-white font-head font-extrabold uppercase text-xl mt-2 leading-tight">Veteran-Owned Exterior Cleaning You Can Count On</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">{c.homepage_hero_image ? "Custom image active" : "Using the default hero image"}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MediaPicker
            label="Desktop Image"
            value={c.homepage_hero_image || ""}
            onChange={(ref) => setC((prev) => ({ ...prev, homepage_hero_image: ref }))}
            previewClass="h-28"
            testid="hero-desktop"
          />
          <ASelect label="Desktop Position" value={c.homepage_hero_position || "center"} options={POS_OPTIONS} onChange={(e) => setC({ ...c, homepage_hero_position: e.target.value })} />
          <MediaPicker
            label="Mobile Image (optional)"
            value={c.homepage_hero_mobile_image || ""}
            onChange={(ref) => setC((prev) => ({ ...prev, homepage_hero_mobile_image: ref }))}
            previewClass="h-28"
            testid="hero-mobile"
          />
          <ASelect label="Mobile Position" value={c.homepage_hero_mobile_position || "center"} options={POS_OPTIONS} onChange={(e) => setC({ ...c, homepage_hero_mobile_position: e.target.value })} />
        </div>

        <div>
          <label className={labelCls}>Overlay Darkness (text readability): {c.homepage_hero_overlay || 0}%</label>
          <input type="range" min="0" max="80" value={c.homepage_hero_overlay || 0} onChange={(e) => setC({ ...c, homepage_hero_overlay: Number(e.target.value) })} className="w-full" data-testid="hero-overlay" />
        </div>

        <div className="flex gap-2"><Btn variant="ghost" onClick={reset} data-testid="hero-reset">Reset to Default</Btn></div>
        <p className="text-xs text-gray-400">After selecting, click <b>Save Changes</b> at the top to publish. Future image changes need no code or redeploy.</p>
      </div>
    </Card>
  );
};

const Group = ({ title, children }) => (
  <Card title={title}><div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div></Card>
);

export default function Content() {
  const [c, setC] = useState(null);
  const [saving, save] = useSave();
  useEffect(() => { api.get("/admin/content").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return null;

  const set = (k) => (e) => setC({ ...c, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = () => save(() => api.patch("/admin/content", c));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Website Content</h1>
        <Btn onClick={submit} loading={saving} data-testid="content-save">Save Changes</Btn>
      </div>

      <Group title="Business Info">
        <AInput label="Business Name" value={c.business_name || ""} onChange={set("business_name")} />
        <AInput label="Phone" value={c.phone || ""} onChange={set("phone")} />
        <AInput label="Email" value={c.email || ""} onChange={set("email")} />
        <AInput label="Location" value={c.location || ""} onChange={set("location")} />
        <AInput label="Business Hours" value={c.business_hours || ""} onChange={set("business_hours")} />
        <AInput label="Announcement Bar" value={c.announcement || ""} onChange={set("announcement")} />
      </Group>

      <Group title="Homepage">
        <AInput label="Hero Headline" value={c.hero_headline || ""} onChange={set("hero_headline")} />
        <div className="sm:col-span-2"><ATextarea label="Hero Subtext" value={c.hero_subtext || ""} onChange={set("hero_subtext")} /></div>
        <ATextarea label="Residential Intro" value={c.residential_intro || ""} onChange={set("residential_intro")} />
        <ATextarea label="Commercial Intro" value={c.commercial_intro || ""} onChange={set("commercial_intro")} />
        <div className="sm:col-span-2"><ATextarea label="Service Area Note" value={c.service_area_note || ""} onChange={set("service_area_note")} /></div>
      </Group>

      <LogoCard c={c} setC={setC} />

      <HeroImageCard c={c} setC={setC} />

      <Group title="Owner Section">
        <AInput label="Owner Name (public)" value={c.owner_name || ""} onChange={set("owner_name")} />
        <div>
          <MediaPicker
            label="Owner Photo"
            value={c.owner_photo || ""}
            onChange={(ref) => setC({ ...c, owner_photo: ref })}
            allowCapture
            allowExternalUrl
            previewClass="h-40"
            testid="owner-photo"
          />
        </div>
        <div className="sm:col-span-2"><ATextarea label="Owner Story" value={c.owner_story || ""} onChange={set("owner_story")} /></div>
        <div className="sm:col-span-2"><ATextarea label="Military Details (optional — leave blank until confirmed)" value={c.military_details || ""} onChange={set("military_details")} /></div>
      </Group>

      <Group title="Ghost AI">
        <div className="sm:col-span-2"><ATextarea label="Ghost AI Greeting" value={c.ghost_greeting || ""} onChange={set("ghost_greeting")} /></div>
      </Group>

      <Group title="Financing">
        <AInput label="Financing Note" value={c.financing_note || ""} onChange={set("financing_note")} />
        <AInput label="Financing URL" value={c.financing_url || ""} onChange={set("financing_url")} />
        <div className="sm:col-span-2"><ATextarea label="Financing Disclosure" value={c.financing_disclosure || ""} onChange={set("financing_disclosure")} /></div>
      </Group>

      <Group title="Footer & Credit">
        <AInput label="Footer Credit Text" value={c.footer_credit || ""} onChange={set("footer_credit")} />
        <AInput label="Footer Credit URL (leave blank for text only)" value={c.footer_credit_url || ""} onChange={set("footer_credit_url")} />
        <label className="flex items-center gap-2 text-sm text-[var(--navy)] font-medium">
          <input type="checkbox" checked={c.footer_credit_show !== false} onChange={set("footer_credit_show")} className="w-4 h-4 accent-[var(--action)]" data-testid="credit-toggle" />
          Show footer credit
        </label>
      </Group>

      <SocialLinksCard c={c} setC={setC} />

      <Group title="SEO">
        <AInput label="SEO Title" value={c.seo_title || ""} onChange={set("seo_title")} />
        <div className="sm:col-span-2"><ATextarea label="Meta Description" value={c.seo_description || ""} onChange={set("seo_description")} /></div>
      </Group>

      <div className="flex justify-end"><Btn onClick={submit} loading={saving}>Save Changes</Btn></div>
    </div>
  );
}
